import { useRef, useState } from "react";

/**
 * A number inside a sentence that can be dragged sideways or stepped with the arrow keys, as link numbers are in
 * the app (PRD §10.6: "each link shows its sentence with the number as a slider").
 */
export function Scrub({
	value,
	min,
	max,
	step,
	onChange,
	format,
	label,
	risk,
}: {
	value: number;
	min: number;
	max: number;
	step: number;
	onChange: (v: number) => void;
	format: (v: number) => string;
	label: string;
	risk?: boolean;
}) {
	const origin = useRef<{ x: number; v: number } | null>(null);
	const [active, setActive] = useState(false);
	const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v / step) * step));
	// About one step per 7 px of travel, so the whole range fits in a comfortable sweep.
	const PX = 7;
	return (
		<span
			role="slider"
			tabIndex={0}
			aria-label={label}
			aria-valuemin={min}
			aria-valuemax={max}
			aria-valuenow={value}
			aria-valuetext={format(value)}
			data-active={active}
			className={`scrub ${risk ? "is-risk" : ""}`}
			onPointerDown={(e) => {
				e.preventDefault();
				(e.currentTarget as Element).setPointerCapture(e.pointerId);
				origin.current = { x: e.clientX, v: value };
				setActive(true);
			}}
			onPointerMove={(e) => {
				if (!origin.current) return;
				onChange(clamp(origin.current.v + Math.round((e.clientX - origin.current.x) / PX) * step));
			}}
			onPointerUp={() => {
				origin.current = null;
				setActive(false);
			}}
			onPointerCancel={() => {
				origin.current = null;
				setActive(false);
			}}
			onKeyDown={(e) => {
				const d = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[e.key];
				if (d) {
					e.preventDefault();
					onChange(clamp(value + d * step * (e.shiftKey ? 5 : 1)));
				} else if (e.key === "Home" || e.key === "End") {
					e.preventDefault();
					onChange(e.key === "Home" ? min : max);
				}
			}}
		>
			{format(value)}
		</span>
	);
}
